import { useFormik } from 'formik'
import { useSnackbar } from 'notistack'

import { Operator } from '@img-arena/consoles-api-dde'
import { ConsoleDialog } from '@/components/ConsoleDialog'
import { FormikTextField } from '@/components/inputs/FormikTextField'
import { ddeApi } from '@/service/ddeApi'
import { FormDialogProps } from '@/types/forms'
import { formikSaveButtonProps } from '@/utils/forms'

import { operatorFormValidationSchema } from './operatorForm.validation'

export type DDE_OperatorFormikValues = {
  name: string
  email: string
}

export type DDE_OperatorFormProps = FormDialogProps<
  Omit<Operator, 'createdDate'>,
  undefined,
  keyof DDE_OperatorFormikValues
>

// NOTE: It is not possible to edit existing operator
export const DDEOperatorForm = ({ onClose, onSubmit, open = false }: DDE_OperatorFormProps) => {
  const { enqueueSnackbar } = useSnackbar()

  const formik = useFormik<DDE_OperatorFormikValues>({
    initialValues: {
      name: '',
      email: ''
    },

    validationSchema: operatorFormValidationSchema.DDE,

    onSubmit: async (values, { setSubmitting }) => {
      if (!values.name || !values.email) return

      try {
        setSubmitting(true)

        await ddeApi.endpoints.operators.add({
          email: values.email,
          name: values.name
        })

        onSubmit?.()
        enqueueSnackbar(`Operator ${values.name} has been created`, {
          variant: 'success'
        })
        setSubmitting(false)
        handleClose()
      } catch (e) {
        enqueueSnackbar('Failed to create operator', { variant: 'error' })
        setSubmitting(false)
        // Throw the error again to be caught by dialog and doesn't close it
        throw e
      }
    }
  })

  const handleClose = () => {
    onClose?.()
    formik.resetForm()
  }

  return (
    <ConsoleDialog
      open={open}
      title="Add Operator"
      onClose={handleClose}
      SaveButtonProps={formikSaveButtonProps(formik)}
    >
      <FormikTextField name="name" label="Name" formik={formik} />
      <FormikTextField name="email" label="Email" formik={formik} />
    </ConsoleDialog>
  )
}
